import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Modal } from "../Modal";
import { listCourses } from "../../services/course.service";
import { createEvent, updateEvent, type EventInput } from "../../services/event.service";
import { getApiErrorMessage } from "../../lib/apiClient";
import type { EventRecord, EventStatus } from "../../types";

const EMPTY_FORM = {
  courseId: "",
  name: "",
  description: "",
  eventDate: "",
  startTime: "09:00",
  endTime: "12:00",
  location: "",
  capacity: "",
  status: "DRAFT" as EventStatus,
};

function toFormState(event: EventRecord) {
  return {
    courseId: event.courseId,
    name: event.name,
    description: event.description ?? "",
    eventDate: event.eventDate.slice(0, 10),
    startTime: event.startTime,
    endTime: event.endTime,
    location: event.location,
    capacity: event.capacity ? String(event.capacity) : "",
    status: event.status,
  };
}

export function EventFormModal({
  open,
  onClose,
  initial,
}: {
  open: boolean;
  onClose: () => void;
  initial?: EventRecord | null;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { data: courses } = useQuery({ queryKey: ["courses"], queryFn: listCourses });
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (!open) return;
    setForm(initial ? toFormState(initial) : EMPTY_FORM);
  }, [open, initial]);

  const mutation = useMutation({
    mutationFn: (input: EventInput) => (initial ? updateEvent(initial.id, input) : createEvent(input)),
    onSuccess: () => {
      toast.success(initial ? "Event updated" : "Event created");
      queryClient.invalidateQueries({ queryKey: ["events"] });
      if (initial) queryClient.invalidateQueries({ queryKey: ["events", initial.id] });
      onClose();
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    mutation.mutate({
      courseId: form.courseId,
      name: form.name,
      description: form.description || null,
      eventDate: form.eventDate,
      startTime: form.startTime,
      endTime: form.endTime,
      location: form.location,
      capacity: form.capacity ? Number(form.capacity) : null,
      status: form.status,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? "Edit Event" : "New Event"}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="event-form" className="btn-primary" disabled={mutation.isPending}>
            {mutation.isPending ? "Saving..." : initial ? "Save" : "Create Event"}
          </button>
        </>
      }
    >
      <form id="event-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label" htmlFor="courseId">
            Course
          </label>
          <select
            id="courseId"
            className="input"
            value={form.courseId}
            onChange={(e) => setForm({ ...form, courseId: e.target.value })}
            required
          >
            <option value="">Select a course</option>
            {courses?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} (age {c.ageMin}-{c.ageMax})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="event-name">
            Event name
          </label>
          <input
            id="event-name"
            className="input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
        </div>
        <div>
          <label className="label" htmlFor="event-description">
            Description
          </label>
          <textarea
            id="event-description"
            className="input"
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="label" htmlFor="event-date">
              Date
            </label>
            <input
              id="event-date"
              type="date"
              className="input"
              value={form.eventDate}
              onChange={(e) => setForm({ ...form, eventDate: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="label" htmlFor="start-time">
              Start
            </label>
            <input
              id="start-time"
              type="time"
              className="input"
              value={form.startTime}
              onChange={(e) => setForm({ ...form, startTime: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="label" htmlFor="end-time">
              End
            </label>
            <input
              id="end-time"
              type="time"
              className="input"
              value={form.endTime}
              onChange={(e) => setForm({ ...form, endTime: e.target.value })}
              required
            />
          </div>
        </div>
        <div>
          <label className="label" htmlFor="location">
            Location
          </label>
          <input
            id="location"
            className="input"
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label" htmlFor="capacity">
              Capacity (optional)
            </label>
            <input
              id="capacity"
              type="number"
              className="input"
              value={form.capacity}
              onChange={(e) => setForm({ ...form, capacity: e.target.value })}
              min={1}
            />
          </div>
          <div>
            <label className="label" htmlFor="status">
              Status
            </label>
            <select
              id="status"
              className="input"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as EventStatus })}
            >
              <option value="DRAFT">Draft</option>
              <option value="OPEN">Open</option>
              <option value="CLOSED">Closed</option>
            </select>
          </div>
        </div>
      </form>
    </Modal>
  );
}
